/**
 * Components and helpers for rows
 * @module
 */
import { DAStudyRow, DEV_TYPES, ERow, FieldType, SUPPORTED_ROW_TYPES } from 'services/extract/types'
import StudyContext from './model/StudyContext'
import StudySummaryModel from './model/StudySummaryModel'
import { EntityComparison, isExtractableRow } from './model/comparison'
import { all, and, Button, Context, Div, equal, if as _if, not, or, Span, ReplacedEvent } from 'alkali'
import Icon from 'components/Icon'
import Dropdown, { getRadioItems } from 'components/generic/Dropdown'
import { createExtractRow, lookupRowConfig, toExtractRow } from 'services/extract/generate'
import { detectTermPresence, getStudyRow, queryAnnotationsForType } from 'components/extract/model/extraction'

import * as warnIcon from 'icons/alert-triangle.svg'
import * as addEntry from 'icons/data-file-bars-add.svg'
import * as uneditableEntry from 'icons/data-file-bars-disable.svg'
import * as outcomeIcon from 'icons/data-file-bars.svg'
//import * as editOutcome from 'icons/data-file-bars-edit.svg'
import * as errIcon from 'icons/alert-diamond.svg'
import * as arrowDown from 'icons/selectivity-caret.svg'
import { CTG_SOURCE, EU_SOURCE } from 'components/studies/studyUrls'


const editOutcome = outcomeIcon

export enum Mode {
	EDIT= 'edit',
	ADD = 'add',
	EXTRACT_EDIT = 'extract-edit',
	EXTRACT_ADD = 'extract-add'
}

export async function compState(mode: Mode, row: ERow, rows: ERow[], studyCtx: StudyContext) {
	let valid
	let comparison: EntityComparison<any, DAStudyRow>
	let configurable = true
	if (row.existing) {
		comparison = studyCtx.evalRow(row.section, row.existing?.id)
		configurable = !!comparison.editable
		if (mode == Mode.EDIT) {
			// allow editing RE and CTG groups
			if (configurable && CTG_SOURCE.sources.includes(studyCtx.study.source) == false
							 && EU_SOURCE.sources.includes(studyCtx.study.source) == false) {
				configurable = !!comparison.matches.find(m => m.isEditable && m.isRE)
			}
			valid = configurable
		} else if (mode == Mode.EXTRACT_ADD || mode == Mode.EXTRACT_EDIT) {
			// should be pre-filtering referenceable entities anyway
			valid = comparison.matches.every(m => m.isReferenceable)
			// for now do not allow editing in select mode
			configurable = false
		} else {
			// cannot add an existing row; invalid state
			valid = false
			configurable = false
		}
	} else {
		comparison = await studyCtx.compareRow(row)
		if (mode == Mode.ADD) {
			// if we are adding a new entry, there can't be any existing matches
			valid = !comparison
		} else if (mode == Mode.EXTRACT_ADD) {
			// should be pre-filtering referenceable entities anyway
			valid = !comparison || comparison.matches.every(m => m.isReferenceable)
		} else {
			// cannot edit a non-existent row
			valid = false
			configurable = false
		}
	}
	const otherRows = rows.filter(r => r != row)
	const availableOutcomes = studyCtx.outcomes.filter(studyRow =>
		studyCtx.evalRow('Outcomes', studyRow.id)?.matches?.some(m => m.isReferenceable) &&
		!otherRows.find(r => r.id == studyRow.id)
	)
	return {
		mode,
		valid,
		configurable,
		comparison,
		availableOutcomes
	}
}

const studyOutcomeToStr = o => (o.name || 'Outcome') + (o.time?.description ? ' ' + o.time?.description : '')
const outcomeToStr = o => (o.outcome?.term?.label || 'Outcome') + (o.timepoint?.val ? ` @${o.timepoint.val}` : '')

const CaretIcon = Icon('.caret', { icon: arrowDown })

export class OutcomeStatus extends Div('.outcome-status') {
	created(props) {
		const { outcome, state, addValidation } = props
		const title = all([addValidation, state], (addValidation, state) => {
			const { mode, valid, comparison: { matches = [] } = {} } = state
			const detail = matches?.length ? 'Existing outcomes: ' + matches.map(m => outcomeToStr(m.match)).join('; ') : 'New Outcome'
			let title = ''
			if (!valid || !addValidation.isValid) {
				title += 'Cannot ' + mode.split('-')[0] + ' outcome'
				if (mode == Mode.EXTRACT_ADD && !addValidation.isValid && addValidation.some(s => s)) {
					title += ': ' + addValidation.filter(s => s).join('; ')
				}
				title += '\n'
			}
			title += detail
			return title
		})
		const icon = state.to((state = {}) => {
			const { mode, comparison: { matches = [], uneditable } = {} } = state
			return (mode == Mode.EDIT && uneditable) ? uneditableEntry : matches?.length ? editOutcome : addEntry
		})
		const isSelect = equal(state.property('mode'), Mode.EXTRACT_ADD)
		const stateValid = state.property('valid')
		let dropdown
		const availableOutcomes = state.property('availableOutcomes')
		const selectEnabled = or(availableOutcomes.property('length'), outcome.existing)
		const canSelect = and(isSelect, selectEnabled)

		const StatusIcon = Icon({
			classes: {
				'err-icon': not(stateValid)
			},
			icon,
			title
		})

		props.classes = {
			'status-icon': not(isSelect)
		}
		props.content = isSelect.to(isSelect => [
			isSelect ?
				Button('.inline-input-btn.center-icon-btn.icons-btn', {
					title,
					disabled: not(selectEnabled),
					onclick: (event) => {
						event.preventDefault()
						if (canSelect.valueOf()) {
							dropdown || (dropdown = new Dropdown({
								items: all([availableOutcomes, outcome, outcome.existing], (outcomes, current, existing) => outcomes.map(o => ({
									content: [
										Span({ title: studyOutcomeToStr(o) }, studyOutcomeToStr(o))
									],
									onclick() {
										// TODO: remap outcome cells to preserve existing values?
										const event = new ReplacedEvent()
										event.reassigning = true
										outcome.put(createExtractRow(current.section, {
											...toExtractRow(current.section, o),
											// preserve user cells
											cells: current.cells
										}), event)
									}
								})).concat(existing ? [{
									content: [
										Span({ title: 'Clear selected outcome' }, 'Clear selected outcome')
									],
									onclick() {
										outcome.put(createExtractRow(current.section))
									}
								}] : []))
							}))
							dropdown.show(event)
						}
					}
				}, [StatusIcon, CaretIcon])
				: undefined //StatusIcon
		])
	}
}

export const DevTypeToggle = (outcome, configurable) => {
	function devTypeToggle() {
		const cur = outcome.devType.valueOf() || 'sd'
		outcome.devType.put(DEV_TYPES[(DEV_TYPES.indexOf(cur) + 1) % DEV_TYPES.length])
	}
	const devType = outcome.property('devType')
	const devTypeLabel = devType.to(t => (t || 'sd').toUpperCase())
	const element = configurable.to(d =>
		Span({
			classes: { clickable: !d },
			onclick: d ? undefined : devTypeToggle
		}, devTypeLabel)
	)
	const title = devType.to(t => 'Standard ' + ({
		sd: 'Deviation',
		se: 'Error'
	}[t || 'sd']))
	return { title, element, devTypeLabel, devTypeToggle }
}

export const BinaryPctToggle = (outcome, disabled) => {
	function toggleBinaryPctNum(state?) {
		const pct = isBinaryPct.valueOf()
		if (pct == state) return
		outcome.unit.put(pct ? '' : '%')
	}
	const binaryPctNumToggle = (state?) =>
		e => {
			e.preventDefault()
			toggleBinaryPctNum(state)
		}
	const type = outcome.property('type')
	const binary = type.to(t => t as unknown as FieldType == FieldType.BINARY)
	const unit = outcome.property('unit')
	// reuse the row unit field to represent binary pct/num
	const isBinaryPct = unit.to(u => u == '%')
	const binaryUnitLabel = _if(isBinaryPct, ['%', '#'])
	const title = _if(binary, [binaryUnitLabel, 'Unit'])
	const element = disabled.to(d =>
		Span({
			classes: { hidden: not(binary), clickable: !d },
			onclick: d ? undefined : binaryPctNumToggle()
		}, binaryUnitLabel)
	)
	return { title, element, isBinaryPct, binaryUnitLabel, binaryPctNumToggle }
}

export const OutcomeDetectionIcon = (outcome, summary: StudySummaryModel) => {
	const presence = all([outcome, summary.annotations, summary.studyCtx.queryAnnotations], (outcome, annotations, queryAnnotations) =>
		detectTermPresence(
			outcome?.name?.term ? [outcome.name.term] : [],
			summary.studyCtx.outcomes,
			annotations,
			queryAnnotationsForType(queryAnnotations, 'outcome')
		)
	)
	return Icon({
		title: presence.to(({ missing, inStudy, matchesQuery }) =>
			[
				missing && ('Outcome not found in abstract ' + (inStudy ? '(extracted from full text)' : 'nor full text')),
				!matchesQuery && 'Warning: entered outcome does not matched searched outcome(s) - extraction may not be returned by query'
			].filter(Boolean).join('\n')
		),
		icon: presence.to(({error, warn}) => error ? errIcon : warnIcon),
		classes: {
			'warn-icon': presence.property('warn'),
			'err-icon': presence.property('error'),
			invisible: not(or(presence.property('warn'), presence.property('error')))
		}
	})
}

const toRowTypeLabel = (fieldType, rowType) => fieldType + (rowType ? ('-' + rowType) : '')

export const toRowConfigLabel = row => row.to(({ section, type: fieldType, rowType, valueType }) => {
	const config = lookupRowConfig(section, fieldType, rowType, valueType)
	return config?.displayName || config?.name || toRowTypeLabel(fieldType, rowType)
})

const ROW_TYPE_ITEMS = SUPPORTED_ROW_TYPES.map(({ fieldType, rowType, label }) =>
	({ field: fieldType + '-' + (rowType || ''), label: label || toRowTypeLabel(fieldType, rowType) }))

export class RowTypeButton extends Button('.row-type').with({ type: 'button'}) {
	created(props) {
		let rowTypeDropdown
		const row = props.row
		const rowConfig = all([row.type, row.rowType])
		const rowConfigKey = rowConfig.to(
			([fieldType, rowType]) => fieldType + '-' + (rowType || ''),
			selected => {
				const [fieldType, rowType] = selected.split('-')
				row.type.put(fieldType)
				row.rowType.put(rowType || undefined)
			})
		const rowConfigLabel = toRowConfigLabel(row)
		Object.assign(props, {
			disabled: not(props.editable),
			onclick(e) {
				if (!rowTypeDropdown) {
					rowTypeDropdown = new Dropdown({
						items: getRadioItems(ROW_TYPE_ITEMS, rowConfigKey, value => {
							const e = new Event('rowtype-changed', { bubbles: true })
							e.selected = value
							this.dispatchEvent(e)
						})
					})
				}
				rowTypeDropdown.show(e)
			},
			content: [
				rowConfigLabel,
				Icon('.caret', { icon: arrowDown })
			]
		})

	}
}